// src/components/common/Loader.jsx

import React from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Backdrop,
  Skeleton,
  Stack,
  Card,
  CardContent,
} from "@mui/material";

const Loader = ({ size = "md", fullScreen = false, text = "Loading..." }) => {
  const sizeMap = {
    sm: 24,
    md: 40,
    lg: 56,
    xl: 72,
  };

  const spinner = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress size={sizeMap[size] || 40} />
      {text && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {text}
        </Typography>
      )}
    </Box>
  );

  if (fullScreen) {
    return (
      <Backdrop
        open={true}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        }}
      >
        {spinner}
      </Backdrop>
    );
  }

  return spinner;
};

// Loading overlay for buttons or sections
export const LoadingOverlay = ({ children, loading }) => {
  if (!loading) return children;

  return (
    <Box sx={{ position: "relative" }}>
      <Box sx={{ opacity: 0.5, pointerEvents: "none" }}>{children}</Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} />
      </Box>
    </Box>
  );
};

// Skeleton loader for cards
export const SkeletonLoader = ({ count = 1, type = "card" }) => {
  const skeletons = Array.from({ length: count }, (_, i) => i);

  if (type === "card") {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
          },
          gap: 3,
        }}
      >
        {skeletons.map((i) => (
          <Card key={i} sx={{ borderRadius: 2 }}>
            <Skeleton variant="rectangular" height={180} animation="wave" />
            <CardContent>
              <Skeleton
                variant="text"
                width="75%"
                height={24}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width="50%"
                height={20}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width="66%"
                height={20}
                animation="wave"
              />
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={36}
                  animation="wave"
                  sx={{ borderRadius: 1 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={64}
                  height={36}
                  animation="wave"
                  sx={{ borderRadius: 1 }}
                />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (type === "list") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {skeletons.map((i) => (
          <Card key={i} sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Skeleton
                variant="circular"
                width={48}
                height={48}
                animation="wave"
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton
                  variant="text"
                  width="75%"
                  height={24}
                  animation="wave"
                />
                <Skeleton
                  variant="text"
                  width="50%"
                  height={20}
                  animation="wave"
                />
              </Box>
              <Skeleton
                variant="rectangular"
                width={80}
                height={32}
                animation="wave"
                sx={{ borderRadius: 1 }}
              />
            </Box>
          </Card>
        ))}
      </Box>
    );
  }

  if (type === "table") {
    return (
      <Box>
        {/* Table Header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 2,
            p: 2,
            bgcolor: "grey.50",
            borderRadius: 1,
            mb: 1,
          }}
        >
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton
              key={i}
              variant="text"
              width="90%"
              height={20}
              animation="wave"
            />
          ))}
        </Box>
        {/* Table Rows */}
        {skeletons.map((i) => (
          <Box
            key={i}
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: 2,
              p: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            {Array.from({ length: 6 }, (_, j) => (
              <Skeleton
                key={j}
                variant="text"
                width={j === 0 ? "70%" : "80%"}
                height={20}
                animation="wave"
              />
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  return null;
};

export default Loader;
